// VCIN Brand: Always use VCIN_COLORS from constants.js - never use Tailwind blue classes
import React, { useState, useEffect } from 'react';
import { 
  BarChart3, Calendar, Mail, Loader2, AlertCircle, 
  TrendingUp, Users, MapPin, Clock, Tag, X, CheckCircle, Building2, Download, ChevronDown
} from 'lucide-react';
import api from '../../services/api';
import { VCIN_COLORS } from '../../styles/constants';

// Time period options
const TIME_PERIODS = [
  { value: '7days', label: 'Last 7 Days' },
  { value: '30days', label: 'Last 30 Days' },
  { value: '90days', label: 'Last 90 Days' },
  { value: 'custom', label: 'Custom Range' }
];

// Loading skeleton
function StatSkeleton() {
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm animate-pulse">
      <div className="h-4 w-24 bg-gray-200 rounded mb-4" />
      <div className="h-8 w-16 bg-gray-200 rounded" />
    </div>
  );
}

// Email Modal
function EmailModal({ isOpen, onClose, onSend, loading }) {
  const [email, setEmail] = useState('');

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSend(email);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md" data-testid="email-modal">
          <div className="p-6 border-b border-gray-100 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Email Report</h3>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="recipient@example.com"
                required
                className="w-full h-11 px-4 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
                data-testid="email-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                A summary of platform-wide statistics will be sent to this address.
              </p>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 h-11 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !email}
                className="flex-1 h-11 rounded-lg font-medium text-white flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: VCIN_COLORS.primary }}
                data-testid="send-email-btn"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</>
                ) : (
                  <><Mail className="w-4 h-4" /> Send Report</>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

// Stat Card
function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-500 text-sm font-medium mb-1">{label}</p>
          <p className="text-3xl font-bold text-gray-900">{value}</p>
        </div>
        <div 
          className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
      </div>
    </div>
  );
}

// Bar Chart Component (simple CSS-based)
function SimpleBarChart({ data, labelKey, valueKey, color }) {
  if (!data || data.length === 0) {
    return <p className="text-gray-400 text-sm text-center py-8">No data available</p>;
  }
  
  const maxValue = Math.max(...data.map(d => d[valueKey]));
  
  return (
    <div className="space-y-3">
      {data.map((item, idx) => (
        <div key={idx} className="flex items-center gap-3">
          <div className="w-36 text-sm text-gray-600 truncate" title={item[labelKey]}>
            {item[labelKey]}
          </div>
          <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{ 
                width: `${maxValue > 0 ? (item[valueKey] / maxValue) * 100 : 0}%`,
                backgroundColor: color 
              }}
            />
          </div>
          <div className="w-12 text-sm font-medium text-gray-900 text-right">
            {item[valueKey]}
          </div>
        </div>
      ))}
    </div>
  );
}

// Time of Day Chart
function TimeOfDayChart({ data }) {
  if (!data || data.length === 0) {
    return <p className="text-gray-400 text-sm text-center py-8">No data available</p>;
  }
  
  // Fill in missing hours
  const hourData = Array.from({ length: 24 }, (_, i) => {
    const found = data.find(d => d.hour === i);
    return { hour: i, count: found?.count || 0 };
  });
  
  const maxCount = Math.max(...hourData.map(d => d.count));
  
  return (
    <div className="flex items-end justify-between h-32 gap-1">
      {hourData.map((item) => (
        <div key={item.hour} className="flex-1 flex flex-col items-center">
          <div 
            className="w-full rounded-t transition-all duration-300"
            style={{ 
              height: `${maxCount > 0 ? (item.count / maxCount) * 100 : 0}%`,
              minHeight: item.count > 0 ? '4px' : '0',
              backgroundColor: VCIN_COLORS.primary
            }}
            title={`${item.hour}:00 - ${item.count} reports`}
          />
          {item.hour % 6 === 0 && (
            <span className="text-xs text-gray-500 mt-1">{item.hour}h</span>
          )}
        </div>
      ))}
    </div>
  );
}

export default function SAStatsPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('30days');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [selectedCompanyForReport, setSelectedCompanyForReport] = useState(null);
  const [emailReportType, setEmailReportType] = useState('national');

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let url = `/admin/statistics?period=${period}`;
      if (period === 'custom' && customStart && customEnd) {
        url += `&startDate=${customStart}&endDate=${customEnd}`;
      }
      
      const response = await api.get(url);
      setStats(response.data);
    } catch (err) {
      setError(err.message || 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (period !== 'custom' || (customStart && customEnd)) {
      fetchStats();
    }
  }, [period, customStart, customEnd]);

  const handleEmailReport = async (email) => {
    try {
      setEmailSending(true);
      await api.post('/admin/email-pdf-report', { 
        email, 
        stats,
        reportType: emailReportType,
        companyId: selectedCompanyForReport?.id
      });
      setShowEmailModal(false);
      setEmailSuccess(true);
      setTimeout(() => setEmailSuccess(false), 5000);
    } catch (err) {
      alert(err.message || 'Failed to send report');
    } finally {
      setEmailSending(false);
    }
  };

  const handleDownloadReport = async (reportType, companyId = null) => {
    try {
      setDownloadLoading(true);
      setShowDownloadMenu(false);
      
      const response = await api.post('/admin/download-report', { 
        stats, 
        reportType,
        companyId 
      }, { responseType: 'blob' });
      
      const filename = reportType === 'company' && companyId
        ? `VCIN_Company_Report_${new Date().toISOString().split('T')[0]}.pdf`
        : `VCIN_National_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message || 'Failed to download report');
    } finally {
      setDownloadLoading(false);
    }
  };

  const openEmailModal = (type, company = null) => {
    setEmailReportType(type);
    setSelectedCompanyForReport(company);
    setShowEmailModal(true);
  };

  const formatCategory = (cat) => cat?.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) || 'Unknown';

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-600" />
        <span className="text-red-700">{error}</span>
      </div>
    );
  }

  return (
    <div data-testid="sa-stats-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Platform Statistics</h1>
          <p className="text-gray-500 text-sm mt-1">All Companies Overview</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Download Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowDownloadMenu(!showDownloadMenu)}
              disabled={loading || !stats || downloadLoading}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-gray-700 bg-white border border-gray-300 transition-colors hover:bg-gray-50 disabled:opacity-50"
              data-testid="download-menu-btn"
            >
              {downloadLoading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Downloading...</>
              ) : (
                <><Download className="w-4 h-4" /> Download <ChevronDown className="w-4 h-4" /></>
              )}
            </button>
            {showDownloadMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowDownloadMenu(false)} />
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                  <button
                    onClick={() => handleDownloadReport('national')}
                    className="w-full px-4 py-3 text-left text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-t-lg"
                    data-testid="download-national-btn"
                  >
                    Download National Report
                  </button>
                  <div className="border-t border-gray-100">
                    <p className="px-4 py-2 text-xs text-gray-500 uppercase tracking-wider">Company Reports</p>
                    <div className="max-h-48 overflow-y-auto">
                      {stats?.reportsPerCompany?.map(company => (
                        <button
                          key={company.id}
                          onClick={() => handleDownloadReport('company', company.id)}
                          className="w-full px-4 py-2 text-left text-sm text-gray-600 hover:bg-gray-50"
                        >
                          {company.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          
          {/* Email Dropdown */}
          <div className="relative">
            <button
              onClick={() => openEmailModal('national')}
              disabled={loading || !stats}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-white transition-colors hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: VCIN_COLORS.primary }}
              data-testid="email-report-btn"
            >
              <Mail className="w-4 h-4" />
              Email Report
            </button>
          </div>
        </div>
      </div>

      {/* Success Toast */}
      {emailSuccess && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <span className="text-green-700">Report sent successfully!</span>
        </div>
      )}

      {/* Time Period Filter */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Time Period:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {TIME_PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  period === p.value
                    ? 'text-white'
                    : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                }`}
                style={period === p.value ? { backgroundColor: VCIN_COLORS.primary } : {}}
              >
                {p.label}
              </button>
            ))}
          </div>
          {period === 'custom' && (
            <div className="flex items-center gap-2 ml-auto">
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="h-9 px-3 border border-gray-300 rounded-lg text-sm"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="h-9 px-3 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <StatSkeleton />
          <StatSkeleton />
          <StatSkeleton />
          <StatSkeleton />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <StatCard
              icon={TrendingUp}
              label="Total Reports"
              value={stats?.totalReports || 0}
              color={VCIN_COLORS.primary}
            />
            <StatCard
              icon={Building2}
              label="Companies Reporting"
              value={stats?.reportsPerCompany?.length || 0}
              color="#7C3AED"
            />
            <StatCard
              icon={MapPin}
              label="Sites Reporting"
              value={stats?.reportsPerSite?.length || 0}
              color="#059669"
            />
            <StatCard
              icon={Users}
              label="Active Officers"
              value={stats?.reportsByOfficer?.length || 0}
              color="#D97706"
            />
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Reports by Company */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5" style={{ color: '#7C3AED' }} />
                Reports by Company
              </h3>
              <SimpleBarChart 
                data={stats?.reportsPerCompany} 
                labelKey="name" 
                valueKey="count" 
                color="#7C3AED"
              />
            </div>

            {/* Reports by Site */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5" style={{ color: VCIN_COLORS.primary }} />
                Top Sites
              </h3>
              <SimpleBarChart 
                data={stats?.reportsPerSite} 
                labelKey="name" 
                valueKey="count" 
                color={VCIN_COLORS.primary}
              />
            </div>

            {/* Reports by Officer */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5" style={{ color: '#D97706' }} />
                Top Officers
              </h3>
              <SimpleBarChart 
                data={stats?.reportsByOfficer} 
                labelKey="name" 
                valueKey="count" 
                color="#D97706"
              />
            </div>

            {/* Reports by Threat Level */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5" style={{ color: '#DC2626' }} />
                Reports by Threat Level
              </h3>
              <SimpleBarChart 
                data={stats?.reportsByThreatLevel?.map(r => ({ 
                  name: `Level ${r.level}`, 
                  count: r.count 
                }))} 
                labelKey="name" 
                valueKey="count" 
                color="#DC2626"
              />
            </div>

            {/* Reports by Category */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Tag className="w-5 h-5" style={{ color: '#059669' }} />
                Reports by Category
              </h3>
              <SimpleBarChart 
                data={stats?.reportsByCategory?.map(r => ({ 
                  name: formatCategory(r.category), 
                  count: r.count 
                }))} 
                labelKey="name" 
                valueKey="count" 
                color="#059669"
              />
            </div>

            {/* Reports by Time of Day */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5" style={{ color: VCIN_COLORS.primary }} />
                Reports by Time of Day
              </h3>
              <TimeOfDayChart data={stats?.reportsByTimeOfDay} />
            </div>
          </div>
        </>
      )}

      {/* Email Modal */}
      <EmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSend={handleEmailReport}
        loading={emailSending}
      />
    </div>
  );
}
